/**
 * אשף הנפקת דוח חברים — בורר סוג + אפשרויות (הדפסה / Excel / ZIP לפי כולל)
 */
import React from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, FormControl, FormLabel, Radio, RadioGroup, Stack,
  Typography, Alert, CircularProgress, Chip, Divider, Autocomplete, TextField,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { useMemberReportIssue } from './useMemberReportIssue.js';
import MemberReportIssueDialogNext from './MemberReportIssueDialogNext.jsx';
import {
  DEFAULT_COL_KEYS,
  GROUP_BY_OPTIONS,
} from './memberReportMergeFields.js';
import MemberReportTemplateEditor from './MemberReportTemplateEditor.jsx';
import { formatHebrewDateTime } from '../../../utils/hebrewDate';

const LEGACY_PICKER_ICONS = {
  list: FormatListBulletedIcon, breakdown: PieChartOutlineIcon, card: BadgeIcon, byKolel: FolderZipIcon,
};

/** In the new design the same props render MemberReportIssueDialogNext. */
export default function MemberReportIssueDialog(props) {
  return useCkInside() ? <MemberReportIssueDialogNext {...props} /> : <MemberReportIssueDialogLegacy {...props} />;
}

function MemberReportIssueDialogLegacy(props) {
  const {
    step,
    setStep,
    reportType,
    source,
    setSource,
    selectedCols,
    setSelectedCols,
    includeRegistrations,
    setIncludeRegistrations,
    includeGrades,
    setIncludeGrades,
    contentMode,
    setContentMode,
    kolelSlot,
    setKolelSlot,
    groupBy,
    setGroupBy,
    includeMembers,
    setIncludeMembers,
    examPeriod,
    setExamPeriod,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    templateId,
    setTemplateId,
    editorOpen,
    setEditorOpen,
    history,
    showHistory,
    customColumns,
    customColumnPlacement,
    setCustomColumnPlacement,
    customColumnInput,
    setCustomColumnInput,
    busy,
    error,
    isByKolel,
    needsRelatedFilters,
    showCustomColumns,
    columnOptions,
    typeTemplates,
    pickerOptions,
    loadTemplates,
    loadHistory,
    pickType,
    toggleCol,
    addCustomColumn,
    removeCustomColumn,
    handlePrint,
    handleExcel,
    handleByKolelZip,
    typeTitle,
    open,
    onClose,
    filteredCount,
    selectedIds,
    periods,
  } = useMemberReportIssue(props);
  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={step === 'picker' ? 'md' : 'md'} fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {step === 'picker' ? 'הנפקת דוח' : typeTitle}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {step === 'picker' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                בחרו סוג דוח. עיצוב המכתב ורקע התמונה מוגדרים ב״התאם עיצוב ורקע״ בשלב הבא.
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}>
                {pickerOptions.map((opt) => {
                  const Icon = LEGACY_PICKER_ICONS[opt.key];
                  return (
                    <Box
                      key={opt.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => pickType(opt.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          pickType(opt.key);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        p: 2.25,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
                        minHeight: 118,
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                          boxShadow: 'var(--ck-shadow-1, 0 2px 10px rgba(0,0,0,0.06))',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{
                          width: 44, height: 44, borderRadius: 1.5,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'primary.main', color: 'primary.contrastText',
                          flexShrink: 0,
                        }}>
                          <Icon fontSize="small" />
                        </Box>
                        <Typography fontWeight={700} sx={{ fontSize: '1.05rem' }}>{opt.title}</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {opt.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <Stack spacing={2.5}>
              <FormControl>
                <FormLabel>אוכלוסייה</FormLabel>
                <RadioGroup
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  <FormControlLabel
                    value="filters"
                    control={<Radio size="small" />}
                    label={`לפי הסינון בטבלה (${Number(filteredCount || 0).toLocaleString('he-IL')})`}
                  />
                  <FormControlLabel
                    value="selected"
                    control={<Radio size="small" />}
                    label={`לפי השורות שנבחרו (${selectedIds.length.toLocaleString('he-IL')})`}
                    disabled={!selectedIds.length}
                  />
                  <FormControlLabel value="all" control={<Radio size="small" />} label="כל החברים" />
                </RadioGroup>
              </FormControl>

              {isByKolel && (
                <FormControl>
                  <FormLabel>מפתח לפיצול</FormLabel>
                  <RadioGroup
                    value={kolelSlot}
                    onChange={(e) => setKolelSlot(e.target.value)}
                  >
                    <FormControlLabel
                      value="primary"
                      control={<Radio size="small" />}
                      label='כולל ראשי'
                    />
                    <FormControlLabel
                      value="morning"
                      control={<Radio size="small" />}
                      label='לפנה"צ'
                    />
                    <FormControlLabel
                      value="afternoon"
                      control={<Radio size="small" />}
                      label='אחה"צ'
                    />
                    <FormControlLabel
                      value="both"
                      control={<Radio size="small" />}
                      label='גם וגם — קבצים נפרדים לפנה"צ ולאחה"צ'
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {kolelSlot === 'morning' && 'כל חבר ישויך לקובץ לפי כולל לפנה"צ שלו.'}
                    {kolelSlot === 'afternoon' && 'כל חבר ישויך לקובץ לפי כולל אחה"צ שלו.'}
                    {kolelSlot === 'both' && 'חבר עם שני סלוטים יופיע בשני קבצים (בוקר ואחה"צ).'}
                    {kolelSlot === 'primary' && 'קיבוץ לפי כולל ראשי (כמו היום).'}
                  </Typography>
                </FormControl>
              )}

              {isByKolel && (
                <FormControl>
                  <FormLabel>תוכן בכל קובץ</FormLabel>
                  <RadioGroup
                    value={contentMode}
                    onChange={(e) => setContentMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="members"
                      control={<Radio size="small" />}
                      label="רשימת חברים"
                    />
                    <FormControlLabel
                      value="registrations"
                      control={<Radio size="small" />}
                      label="רשימת הרשמות"
                    />
                    <FormControlLabel
                      value="both"
                      control={<Radio size="small" />}
                      label="שניהם (חברים + הרשמות)"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    יופק קובץ Excel נפרד לכל כולל, באריזת ZIP מוכנה לשליחה לגבאים.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    כל קובץ יכלול טבלת חברים מלאה עם העמודות שנבחרו.
                  </Typography>
                </FormControl>
              )}

              {!isByKolel && (
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography fontWeight={700}>עיצוב מכתב ורקע</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        בחרו תבנית טקסט. להתאמת תמונת הרקע והמרווחים — לחצו ״התאם עיצוב ורקע״.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditNoteIcon />}
                      onClick={() => setEditorOpen(true)}
                    >
                      התאם עיצוב ורקע
                    </Button>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={typeTemplates}
                    getOptionLabel={(o) => o.name || ''}
                    value={typeTemplates.find((t) => String(t._id) === String(templateId)) || null}
                    onChange={(_e, v) => setTemplateId(v ? String(v._id) : '')}
                    renderInput={(params) => (
                      <TextField {...params} label="תבנית" placeholder="ללא / ברירת מחדל" />
                    )}
                  />
                  {(reportType === 'list' || reportType === 'breakdown') && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
                      בהדפסה: עמוד המכתב יוצא עם רקע המכתב; טבלת הנתונים בעמוד הבא.
                    </Alert>
                  )}
                  {reportType === 'card' && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
                      כל כרטיס נבחן יוצא על עמוד A4 עם אותה תבנית ורקע מכתב.
                    </Alert>
                  )}
                </Box>
              )}

              {reportType === 'breakdown' && (
                <Box>
                  <Autocomplete
                    size="small"
                    options={GROUP_BY_OPTIONS}
                    getOptionLabel={(o) => o.label}
                    value={GROUP_BY_OPTIONS.find((o) => o.value === groupBy) || null}
                    onChange={(_e, v) => setGroupBy(v?.value || 'city')}
                    renderInput={(params) => <TextField {...params} label="פילוח לפי" />}
                    disableClearable
                  />
                  <FormControlLabel
                    control={<Checkbox checked={includeMembers} onChange={(e) => setIncludeMembers(e.target.checked)} />}
                    label="כלול שמות חברים בכל קבוצה"
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}

              {(contentMode !== 'registrations' || !isByKolel) && (
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography fontWeight={600}>עמודות לייצוא</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setSelectedCols(new Set(columnOptions.map((c) => c.key)))}>הכל</Button>
                      <Button size="small" onClick={() => setSelectedCols(new Set(DEFAULT_COL_KEYS))}>ברירת מחדל</Button>
                    </Stack>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 160, overflow: 'auto' }}>
                    {columnOptions.map((c) => (
                      <Chip
                        key={c.key}
                        label={c.label}
                        size="small"
                        color={selectedCols.has(c.key) ? 'primary' : 'default'}
                        variant={selectedCols.has(c.key) ? 'filled' : 'outlined'}
                        onClick={() => toggleCol(c.key)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {showCustomColumns && (
                <Box>
                  <Typography fontWeight={600} sx={{ mb: 0.5 }}>עמודות נוספות למילוי ידני</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    יופיעו בטבלה כעמודות ריקות — למילוי גביה, הערות וכדומה
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="שם עמודה"
                      value={customColumnInput}
                      onChange={(e) => setCustomColumnInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomColumn();
                        }
                      }}
                      inputProps={{ maxLength: 40 }}
                      disabled={customColumns.length >= 10}
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addCustomColumn}
                      disabled={!customColumnInput.trim() || customColumns.length >= 10}
                      sx={{ flexShrink: 0 }}
                    >
                      הוסף
                    </Button>
                  </Stack>
                  {customColumns.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {customColumns.map((label) => (
                          <Chip
                            key={label}
                            label={label}
                            size="small"
                            onDelete={() => removeCustomColumn(label)}
                          />
                        ))}
                      </Box>
                      <FormControl>
                        <FormLabel>מיקום העמודות הנוספות</FormLabel>
                        <RadioGroup
                          row
                          value={customColumnPlacement}
                          onChange={(e) => setCustomColumnPlacement(e.target.value)}
                        >
                          <FormControlLabel
                            value="start"
                            control={<Radio size="small" />}
                            label="בתחילת הטבלה"
                          />
                          <FormControlLabel
                            value="end"
                            control={<Radio size="small" />}
                            label="בסוף הטבלה"
                          />
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}
                </Box>
              )}

              <Divider />

              {!isByKolel && (
                <Box>
                  <Typography fontWeight={600} sx={{ mb: 1 }}>נתונים קשורים</Typography>
                  <FormControlLabel
                    control={<Checkbox checked={includeRegistrations} onChange={(e) => setIncludeRegistrations(e.target.checked)} />}
                    label="רישומים לבחינות"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={includeGrades} onChange={(e) => setIncludeGrades(e.target.checked)} />}
                    label="ציונים"
                  />
                </Box>
              )}

              {needsRelatedFilters && (
                <Stack spacing={1.5}>
                  <Autocomplete
                    size="small"
                    options={periods}
                    getOptionLabel={(o) => o.name || ''}
                    value={examPeriod}
                    onChange={(_e, v) => setExamPeriod(v)}
                    renderInput={(params) => <TextField {...params} label="מועד (אופציונלי)" />}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="מתאריך"
                      InputLabelProps={{ shrink: true }}
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="עד תאריך"
                      InputLabelProps={{ shrink: true }}
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                </Stack>
              )}

              {isByKolel && busy && (
                <Alert severity="info">
                  מפיק קבצים לפי כולל — ייצוא PDF לכ־80 כוללים עשוי להימשך דקה או יותר…
                </Alert>
              )}

              <Box>
                <Button size="small" onClick={loadHistory}>היסטוריית הנפקות</Button>
                {showHistory && (
                  <Box sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                    {!history.length && (
                      <Typography variant="body2" color="text.secondary">אין הנפקות קודמות</Typography>
                    )}
                    {history.slice(0, 10).map((h) => (
                      <Typography key={h._id} variant="caption" display="block" sx={{ mb: 0.5 }}>
                        {formatHebrewDateTime(h.issuedAt)} · {h.reportType} · {h.outputFormat} · {h.memberCount} חברים · {h.issuedBy}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          {step === 'options' && (
            <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('picker')} disabled={busy}>
              חזרה
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={busy}>ביטול</Button>
          {step === 'options' && !isByKolel && (
            <>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                onClick={handleExcel}
                disabled={busy}
              >
                ייצוא Excel
              </Button>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                onClick={handlePrint}
                disabled={busy}
              >
                הדפס / PDF
              </Button>
            </>
          )}
          {step === 'options' && isByKolel && (
            <>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} /> : <FolderZipIcon />}
                onClick={() => handleByKolelZip('xlsx-zip')}
                disabled={busy}
              >
                ייצוא Excel (ZIP)
              </Button>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                onClick={() => handleByKolelZip('pdf-zip')}
                disabled={busy}
              >
                ייצוא PDF (ZIP)
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <MemberReportTemplateEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          loadTemplates(reportType);
        }}
        initialTargetType={reportType === 'byKolel' ? 'list' : reportType}
      />
    </>
  );
}
